'use client';

import { type MutableRefObject, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { utilsStyles } from '../../../..';
import { ScrollFade } from '../../../../components/ui';
import type { VariantType } from '../../../../types';
import type { LotteryCustomOption, LotteryDrawHistoryLineup, LotteryGoods, LotteryPrize } from '../../types';
import { createStagingInfo, odds, pickText, totalStock } from '../../utils';
import LotteryGoodsList from '../LotteryGoodsList/LotteryGoodsList';
import LotteryPrizeStaging, { type LotteryPrizeStagingHandler } from '../LotteryPrizeStaging/LotteryPrizeStaging';
import styles from './LotteryPrizeList.module.scss';

export interface LotteryPrizeListProps {
  lotteryStatus: string;
  prizes: Array<LotteryPrize>;
  lotteryDrawHistory?: Array<LotteryDrawHistoryLineup>;
  backgroundColor?: string;
  hideOdds?: boolean;
  isHideStaging?: boolean;
  customOptions?: LotteryCustomOption;
  locale?: string;
  variant?: VariantType;
  WGetPrizeLabel?: React.ComponentType;
  isGotGoods: (lineupId: number, goods: LotteryGoods, lotteryDrawHistory?: Array<LotteryDrawHistoryLineup>) => boolean;
  formatCurrency: (amount: number) => string;
  LOTTERY_STATUS: { later: { value: string } };
  getLabelSrc: string;
}

function LotteryPrizeList({
  lotteryStatus,
  prizes,
  lotteryDrawHistory,
  backgroundColor,
  hideOdds = false,
  isHideStaging = false,
  customOptions,
  locale = 'ja',
  variant = 'default',
  WGetPrizeLabel,
  isGotGoods,
  formatCurrency,
  LOTTERY_STATUS,
  getLabelSrc,
}: LotteryPrizeListProps) {
  const stagingRef = useRef<LotteryPrizeStagingHandler>();
  const { t } = useTranslation();

  const fadeinCallback = (playId: number) => {
    // stagingRankのアニメーション発火
    stagingRef.current?.play(playId);
  };

  const showRankAnimation = () => {
    if (isHideStaging) return false;
    return customOptions?.hidePrizeLabel !== '1';
  };

  const generateStyle = (prize: LotteryPrize) => {
    if (!prize.stagingKey.includes('A')) return {};
    return {
      background: backgroundColor,
    };
  };

  const shouldHideOdds = () => {
    // hideOddsプロパティが直接指定されている場合はそちらを優先
    if (hideOdds) return true;
    // customOptionsによる制御
    if (customOptions?.hideOdds === '1') return true;
    return false;
  };

  const getLocalizedText = (textJa: string, textEn?: string) => {
    if (!textEn) return textJa;
    return pickText(locale as 'ja' | 'en', textJa, textEn);
  };

  return (
    <>
      <ul className={styles.componentStyle}>
        {prizes.map((prize) => (
          <ScrollFade tag={'li'} key={`prize_${prize.id}`} fadeinCallback={() => fadeinCallback(prize.id)}>
            <div className={styles.itemCard} style={generateStyle(prize)}>
              {/* 在庫・確率 */}
              <div className={styles.statusWrapper}>
                {totalStock(prize.goods) === 0 && (
                  <p className={styles.outStock}>{pickText(locale as 'ja' | 'en', '品切れ', t('soldOut'))}</p>
                )}
                {!shouldHideOdds() && (
                  <p data-testid='prize-odds'>
                    {pickText(locale as 'ja' | 'en', '当選確率', t('odds'))}:&nbsp;
                    {odds(prize.id, prize.odds, lotteryDrawHistory)}%
                  </p>
                )}
              </div>
              {/* ランクアニメーション */}
              {showRankAnimation() &&
                lotteryPrizeStaging(
                  prize.id,
                  prize.stagingKey,
                  stagingRef,
                  customOptions?.useWGetPrizeDisplay === '1',
                  variant,
                  WGetPrizeLabel,
                )}

              {/* 景品ラベル名 */}
              <div className={`${styles.itemName} ${utilsStyles.textLarge} ${utilsStyles.textBold}`}>
                {getLocalizedText(prize.name, prize.categoryName_en)}
              </div>
              {/* 景品ラベル詳細 */}
              <div className={`${styles.itemDescription} ${utilsStyles.textSmall}`}>
                {getLocalizedText(prize.description, prize.description_en)}
              </div>
              {/* 景品一覧 */}
              <LotteryGoodsList
                goodsList={prize.goods}
                linupId={prize.id}
                lotteryDrawHistory={lotteryDrawHistory}
                liKeyPrefix={'prize_goods'}
                lotteryStatus={lotteryStatus}
                customOptions={customOptions}
                isGotGoods={isGotGoods}
                formatCurrency={formatCurrency}
                LOTTERY_STATUS={LOTTERY_STATUS}
                getLabelSrc={getLabelSrc}
              />
            </div>
          </ScrollFade>
        ))}
      </ul>
    </>
  );
}

function lotteryPrizeStaging(
  prizeId: number,
  stagingKey: string,
  ref: MutableRefObject<LotteryPrizeStagingHandler | undefined>,
  isWGet?: boolean,
  variant?: VariantType,
  WGetPrizeLabel?: React.ComponentType,
) {
  if (isWGet && WGetPrizeLabel) return <WGetPrizeLabel />;
  const stagingInfo = createStagingInfo(stagingKey);
  return (
    <div className={styles.itemStaging}>
      <LotteryPrizeStaging
        id={prizeId}
        stagingRank={stagingInfo.stagingRank}
        stagingText={stagingInfo.stagingText}
        customRef={ref}
        variant={variant}
      />
    </div>
  );
}

export default LotteryPrizeList;
